import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react'
import type { FlatList, NativeScrollEvent, NativeSyntheticEvent, ScrollView, SectionList } from 'react-native'

type TVisibilityContextValue = {
  addScrollListener: (listener: () => void) => () => void
}

export const ScrollListenerContext = createContext<TVisibilityContextValue | null>(null)

export const useScrollListenerContext = () => {
  const context = useContext(ScrollListenerContext)
  if (!context) {
    throw new Error('useScrollContext must be used within a ScrollProvider')
  }
  return context
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type TAnyScrollElement = ScrollView | FlatList | SectionList<any, any>

type TScrollListenerProviderProps<T extends TAnyScrollElement> = {
  children: (props: {
    onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
    ref: React.RefObject<T | null>
    scrollEventThrottle: number
  }) => ReactNode
}

export const ScrollListenerProvider = <T extends TAnyScrollElement = ScrollView>({
  children,
}: TScrollListenerProviderProps<T>) => {
  const scrollViewRef = useRef<T>(null)
  const listeners = useRef<Set<() => void>>(new Set())

  const addScrollListener = useCallback((listener: () => void) => {
    listeners.current.add(listener)
    return () => {
      listeners.current.delete(listener)
    }
  }, [])

  const handleScroll = useCallback((_event: NativeSyntheticEvent<NativeScrollEvent>) => {
    listeners.current.forEach((listener) => listener())
  }, [])

  const contextValue = useMemo(() => ({ addScrollListener }), [addScrollListener])

  return (
    <ScrollListenerContext.Provider value={contextValue}>
      {children({ ref: scrollViewRef, onScroll: handleScroll, scrollEventThrottle: 100 })}
    </ScrollListenerContext.Provider>
  )
}
